/**
 * Contracts for the canonical Parcel-native tool registry.
 *
 * This package is imported by the browser bundle, the Spec Worker, the MCP
 * Worker, and Node tests, so nothing here may reach for `node:*`, a database,
 * or a network client. It is data and pure functions only.
 */
import type { SpecToolDecision } from '@parcel/types';

export type JsonSchema = Record<string, unknown>;

/**
 * MCP tool annotations: display and telemetry hints for a client. Parcel
 * authorization never trusts them, and no native descriptor carries them.
 */
export interface ToolAnnotations {
  readOnlyHint?: boolean;
  destructiveHint?: boolean;
  idempotentHint?: boolean;
  openWorldHint?: boolean;
}

/**
 * The model-facing MCP contract. Shape-compatible with the descriptor the MCP
 * server has always advertised, so moving ownership here changes no wire bytes.
 */
export interface ToolDescriptor {
  name: string;
  description: string;
  inputSchema: {
    type: string;
    properties: Record<string, unknown>;
    required?: string[];
    additionalProperties?: boolean;
    // Top-level JSON Schema combinators for cross-field constraints such as
    // "account_id or match". Most tools only use type/properties.
    anyOf?: unknown[];
    oneOf?: unknown[];
  };
  annotations?: ToolAnnotations;
}

/**
 * What a call actually does to Parcel state. `read` never mutates;
 * `reversible_write` can be undone by the member who made it;
 * `destructive` removes something a member cannot trivially restore; and
 * `admin_security` changes who can reach the workspace at all.
 */
export type NativeToolEffect = 'read' | 'reversible_write' | 'destructive' | 'admin_security';

/**
 * OAuth scopes the Developer MCP grant can carry. `skills:*` are advertised
 * from launch so a client that already consented does not have to re-consent
 * when PR 15 ships skills.
 */
export type ParcelMcpScope =
  | 'data:read'
  | 'workspace:read'
  | 'workspace:write'
  | 'skills:read'
  | 'skills:write'
  | 'knowledge:read'
  | 'knowledge:write'
  | 'workspace:admin';

export const PARCEL_MCP_SCOPES: readonly ParcelMcpScope[] = [
  'data:read',
  'workspace:read',
  'workspace:write',
  'skills:read',
  'skills:write',
  'knowledge:read',
  'knowledge:write',
  'workspace:admin',
] as const;

export type NativeToolCategory =
  | 'data'
  | 'fields'
  | 'workspace'
  | 'views'
  | 'subscriptions'
  | 'members'
  | 'feedback'
  | 'web'
  | 'enrich'
  | 'knowledge';

/**
 * Retry posture after a call whose outcome is unknown (timeout, disconnect).
 * `safe_retry`: repeating it cannot change the result. `keyed`: only safe
 * behind an idempotency key. `never_retry_unknown`: ask the member to verify.
 */
export type NativeToolIdempotency = 'safe_retry' | 'keyed' | 'never_retry_unknown';

/**
 * What a trusted previewer renders on an approval card. The model never writes
 * approval copy: `subjectFields` names the validated arguments that identify
 * the target, and `sensitiveFields` names the ones a redactor must mask.
 */
export interface NativeToolPreview {
  title: string;
  subjectFields: readonly string[];
  sensitiveFields: readonly string[];
}

/** Which model-facing clients may offer a tool. MCP is the public contract; Spec is in-app. */
export type NativeToolSurface = 'spec' | 'mcp';

export interface NativeToolDefinition {
  /** Stable identifier for storage and audit. Equals the descriptor name. */
  key: string;
  /** Content-derived; changes whenever the callable contract changes. */
  version: string;
  descriptor: ToolDescriptor;
  category: NativeToolCategory;
  effect: NativeToolEffect;
  /** Clients that may offer this tool. A Spec-only tool never reaches tools/list or the public docs. */
  surfaces: readonly NativeToolSurface[];
  defaultPolicy: SpecToolDecision;
  requiredScope: ParcelMcpScope;
  requiredPlan?: 'pro';
  requiredRole: 'member' | 'admin' | 'owner';
  /** Policy can never resolve below this, whatever a workspace configures. */
  registryFloor: SpecToolDecision;
  idempotency: NativeToolIdempotency;
  previewer: string | null;
  redactor: string;
  preview: NativeToolPreview | null;
  /**
   * Multiplexed tools (one name, several operations) classify per call after
   * argument validation. Must fail to the strictest effect on unknown input.
   */
  resolveEffect?: (args: Readonly<Record<string, unknown>>) => NativeToolEffect;
}

/** Who owns the upstream credential for one connection instance. */
export type ConnectionScopingMode = 'workspace' | 'member';

export interface CuratedConnectionTool {
  /** Provider-independent capability name, for example `contact.create`. */
  key: string;
  /** The provider's own tool slug on the wire, for example `HUBSPOT_CREATE_CONTACT`. */
  providerSlug: string;
  description: string;
  effect: NativeToolEffect;
  defaultPolicy: SpecToolDecision;
  registryFloor: SpecToolDecision;
  idempotency: NativeToolIdempotency;
  /** The provider's own input schema, captured from a live MCP tools/list; absent means uncaptured. */
  inputSchema?: Record<string, unknown>;
  /** Set once a captured MCP tools/list response exists; absent means unverified, not matched. */
  inputSchemaFingerprint?: string;
  /** True when no admin has classified this tool. Absent on every curated tool. */
  unreviewed?: boolean;
  /** Content-derived; set by the builder, never authored by hand. */
  version: string;
}

/**
 * Composio's own toolkit taxonomy, mirrored verbatim including its near-duplicates
 * (analytics_data vs data_analytics, and the three marketing-ish rows). Captured from the
 * console category list, which is why it may trail theirs: a toolkit we curate whose category
 * is missing here fails the build in buildCatalogEntry, which is the intended way to notice.
 *
 * Display only. Categories carry no authority, so they stay out of
 * ConnectionCatalogEntry.version and recategorising never invalidates a live grant.
 */
export const CONNECTION_CATEGORIES = [
  'developer_tools_devops',
  'collaboration_communication',
  'ai_machine_learning',
  'document_file_management',
  'productivity_project_management',
  'crm',
  'analytics_data',
  'entertainment_media',
  'education_lms',
  'design_creative_tools',
  'marketing_social_media',
  'scheduling_booking',
  'ecommerce',
  'finance_accounting',
  'sales_customer_support',
  'hr_recruiting',
  'social_media',
  'workflow_automation',
  'data_analytics',
  'advertising_marketing',
] as const;

export type ConnectionCategory = (typeof CONNECTION_CATEGORIES)[number];

export interface ConnectionCatalogEntry {
  key: string;
  name: string;
  description: string;
  icon: string;
  /** One or more, in display order. A toolkit can legitimately sit in several. */
  categories: readonly ConnectionCategory[];
  provider: 'composio' | 'custom';
  providerToolkit: string;
  scopingModes: readonly ConnectionScopingMode[];
  /** Policy can never resolve below this on a shared workspace connection. */
  sharedPolicyFloor: SpecToolDecision;
  /** OAuth scopes requested at consent; a provider default cannot be relied on. */
  requiredScopes: readonly string[];
  /** Plain-language disclosure of what requiredScopes grants; set only for a grant broad enough
      to need explaining. Excluded from version, same as description/name/icon/categories. */
  accessNote?: string;
  tools: readonly CuratedConnectionTool[];
  /** Hashes scoping, policy, requiredScopes, and each tool's version; excludes display copy,
      categories, and accessNote. */
  version: string;
}
